// @ts-nocheck
import React, { useState, useEffect, useRef } from "react";
import { db } from "../../src/firebase/firebase";
import {
  collection,
  doc,
  addDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  increment,
  setDoc,
  getDoc,
} from "firebase/firestore";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../hooks/useRedux";
import ChatHeader from "../components/ChatHeader";
import { BASE_URL } from "../Constants/BaseUrl";
import CryptoJS from "crypto-js";
import { fetchUserPublicProfile } from "../store/postsSlice";
import { toast } from "react-toastify";
import { Lock } from "lucide-react";

function generateRandomKey() {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-=[]{}|;:,.<>?";
  let key = "";
  for (let i = 0; i < 32; i++) {
    key += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return key;
}

const POLL_INTERVAL_MS = 5000;

function generateFirebaseId() {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let autoId = "";
  for (let i = 0; i < 20; i++) {
    autoId += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return autoId;
}

const ChatScreen = ({ user: propUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const {
    chatId,
    otherUserId: rawOtherUserId,
    otherUser,
    user: stateUser,
  } = location.state || {};

  const reduxUser = useAppSelector((state) => state.user);
  const user = propUser || stateUser || reduxUser || null;
  const normalizedUserId = String(user?.user_id);
  const otherUserId = rawOtherUserId?.user_id ?? rawOtherUserId;

  console.log("ChatScreen user:", JSON.stringify(user, null, 2));
  console.log("ChatScreen other user:", JSON.stringify(otherUser, null, 2));

  const [currentChatId, setCurrentChatId] = useState(
    chatId || generateFirebaseId(),
  );
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [previewImage, setPreviewImage] = useState(null);
  const [fullScreenImage, setFullScreenImage] = useState(null);
  const [encryptionKey, setEncryptionKey] = useState("");
  const messagesEndRef = useRef(null);
  const [isPrivateProfile, setIsPrivateProfile] = useState(false);
  const userDetails = useAppSelector((state) => state.posts.userPublicProfile);

  const liveOtherUser = userDetails;

  const senderName = (() => {
    const first =
      liveOtherUser?.athlete_info?.first_name ||
      liveOtherUser?.coach_info?.first_name ||
      liveOtherUser?.first_name ||
      otherUser?.athlete_info?.first_name ||
      otherUser?.first_name ||
      otherUser?.username;
    const last =
      liveOtherUser?.athlete_info?.last_name ||
      liveOtherUser?.coach_info?.last_name ||
      liveOtherUser?.last_name ||
      otherUser?.athlete_info?.last_name ||
      otherUser?.last_name;

    if (first && last) return `${first} ${last}`;
    return first || last || "Unknown User";
  })();

  const senderPicture = (() => {
    const pic = liveOtherUser?.profile_picture || otherUser?.profile_picture;
    if (!pic)
      return "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQfj3Bm37Nn_rBQHkIzxnpmGMv3AnLWNYvA1_GwXzebfQ7rxLHl0fRsKo6mIi1SmoOiCL4&usqp=CAU";
    return pic.startsWith("https") ? pic : `${BASE_URL}/${pic}`;
  })();

  console.log("ChatScreen userDetails:", JSON.stringify(userDetails, null, 2));

  // Fetch encryption key from Firestore
  useEffect(() => {
    const fetchEncryptionKey = async () => {
      try {
        const chatRef = doc(db, "chats", currentChatId);
        const chatDoc = await getDoc(chatRef);
        if (chatDoc.exists()) {
          const data = chatDoc.data();
          if (data?.encryptionKey) {
            setEncryptionKey(data.encryptionKey);
            console.log("Fetched encryption key:", data.encryptionKey);
          } else {
            const newKey = generateRandomKey();
            await setDoc(chatRef, { encryptionKey: newKey }, { merge: true });
            setEncryptionKey(newKey);
            console.log("Generated and saved new encryption key:", newKey);
          }
        } else {
          const newKey = generateRandomKey();
          setEncryptionKey(newKey);
          console.log("Generated encryption key for new chat:", newKey);
        }
      } catch (error) {
        console.error("Error fetching encryption key:", error);
      }
    };

    fetchEncryptionKey();
  }, [currentChatId]);

  useEffect(() => {
    if (!otherUserId) return;

    const uid = String(otherUserId);

    // initial fetch
    dispatch(fetchUserPublicProfile(uid));

    const interval = setInterval(() => {
      dispatch(fetchUserPublicProfile(uid));
    }, POLL_INTERVAL_MS);

    const onVisibilityChange = () => {
      if (!document.hidden) dispatch(fetchUserPublicProfile(uid));
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [dispatch, otherUserId]);

  useEffect(() => {
    if (userDetails?.profile_visibility === "private") {
      setIsPrivateProfile(true);
    } else {
      setIsPrivateProfile(false);
    }
  }, [userDetails?.profile_visibility]);

  useEffect(() => {
    const nowPrivate = userDetails?.profile_visibility === "private";
    if (nowPrivate !== isPrivateProfile) {
      setIsPrivateProfile(nowPrivate);
      if (nowPrivate) {
        toast.warn("This account is private and you cannot access it.", {
          autoClose: 5000,
        });
      }
    }
  }, [userDetails?.profile_visibility, isPrivateProfile]);

  // Function to encrypt text
  const encryptMessage = (text) => {
    if (!text || !encryptionKey) {
      console.warn(
        "No text or encryption key, returning unencrypted:",
        text,
        encryptionKey,
      );
      return text;
    }
    try {
      const encrypted = CryptoJS.AES.encrypt(text, encryptionKey).toString();
      console.log("Encrypted text:", encrypted);
      return encrypted;
    } catch (error) {
      console.error("Encryption error:", error);
      return text;
    }
  };

  // Function to decrypt text with fallback for unencrypted or legacy messages
  const decryptMessage = (text) => {
    if (!text || !encryptionKey) {
      console.warn(
        "No text or encryption key, returning as-is:",
        text,
        encryptionKey,
      );
      return text;
    }
    if (text.startsWith("U2FsdGVkX1")) {
      try {
        const bytes = CryptoJS.AES.decrypt(text, encryptionKey);
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);
        if (decrypted) {
          console.log("Decrypted text:", decrypted);
          return decrypted;
        } else {
          console.warn(
            "Decryption produced empty result, treating as unencrypted:",
            text,
          );
          return text;
        }
      } catch (error) {
        console.error("Decryption error:", error);
        return text;
      }
    } else {
      console.log("Text is unencrypted or legacy:", text);
      return text;
    }
  };

  useEffect(() => {
    if (!encryptionKey) return;

    const q = query(
      collection(db, "chats", currentChatId, "messages"),
      orderBy("createdAt", "asc"),
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setMessages(
          snapshot.docs.map((doc) => {
            const data = doc.data();
            // Use user._id if available, fallback to user.id
            const messageUserId = data.user?._id ?? data.user?.id;
            const isSender = String(messageUserId) === normalizedUserId;
            console.log(
              `Message ID: ${doc.id}, msg.user._id: ${
                data.user?._id
              } (type: ${typeof data.user?._id}), msg.user.id: ${
                data.user?.id
              } (type: ${typeof data.user
                ?.id}), user.user_id: ${normalizedUserId} (type: ${typeof normalizedUserId}), isSender: ${isSender}`,
            );
            return {
              id: doc.id,
              ...data,
              text: data.text ? decryptMessage(data.text) : data.text,
              isSender, // Store isSender for rendering
            };
          }),
        );
      },
      (error) => {
        console.error("Error fetching messages:", error);
        alert("Failed to load messages. Please try again.");
      },
    );
    return () => unsubscribe();
  }, [currentChatId, encryptionKey, normalizedUserId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (isPrivateProfile) {
      alert("This user's profile is private. You cannot send messages.");
      return;
    }
    if (!newMessage.trim() && !previewImage) return;

    // Validate user object with fallbacks
    if (!user || !normalizedUserId) {
      console.error("User data is incomplete or missing:", user);
      alert("User data is missing. Please log in again.");
      return;
    }

    const userData = {
      user_id: normalizedUserId,
      username: user.username || "Anonymous",
      profile_picture:
        user.profile_picture ||
        "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQfj3Bm37Nn_rBQHkIzxnpmGMv3AnLWNYvA1_GwXzebfQ7rxLHl0fRsKo6mIi1SmoOiCL4&usqp=CAU",
    };

    try {
      const chatRef = doc(db, "chats", currentChatId);
      const messageRef = collection(chatRef, "messages");

      const messageData = {
        text: newMessage ? encryptMessage(newMessage) : "",
        createdAt: serverTimestamp(),
        user: {
          _id: userData.user_id, // Use _id to match mobile app
          id: userData.user_id, // Include id for backward compatibility
          name: userData.username,
          avatar: userData.profile_picture,
        },
        image: previewImage || null,
      };

      await addDoc(messageRef, messageData);

      const chatDoc = await getDoc(chatRef);
      if (chatDoc.exists()) {
        await updateDoc(chatRef, {
          lastMessage: {
            text: newMessage
              ? encryptMessage(newMessage)
              : messageData.image
                ? "📷 Image"
                : "",
            timestamp: serverTimestamp(),
            user: messageData.user,
          },
          [`unseenCounts.${otherUserId}`]: increment(1),
        });
      } else {
        await setDoc(chatRef, {
          users: [userData.user_id, otherUserId],
          usersDetails: [
            {
              user_id: userData.user_id,
              first_name: userData.username,
              profile_picture: userData.profile_picture,
            },
            {
              user_id: otherUserId,
              first_name: otherUser?.username || "Unknown",
              profile_picture: otherUser?.profile_picture || "",
            },
          ],
          lastMessage: {
            text: newMessage
              ? encryptMessage(newMessage)
              : messageData.image
                ? "📷 Image"
                : "",
            timestamp: serverTimestamp(),
            user: messageData.user,
          },
          unseenCounts: {
            [userData.user_id]: 0,
            [otherUserId]: 1,
          },
          encryptionKey: encryptionKey,
        });
      }

      setNewMessage("");
      setPreviewImage(null);
    } catch (error) {
      console.error("Error sending message:", error);
      alert(`Failed to send message: ${error.message}`);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => setPreviewImage(event.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleBackPress = () => {
    navigate("/chat-list");
  };

  return (
    <div className="flex flex-col min-h-screen h-[100dvh] bg-black text-white">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-black border-b border-gray-700">
        <ChatHeader
          userName={senderName}
          profilePicture={senderPicture}
          onBackPress={handleBackPress}
        />
      </div>

      {/* Main Chat Body */}
      <div className="flex-1 flex flex-col bg-black">
        {isPrivateProfile ? (
          /* Private Profile Locked View - Matches React Native exactly */
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
            <div className="mb-4">
              <Lock className="text-gray-500" size={70} />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">
              This user's profile is private.
            </h2>
            <p className="text-sm text-gray-400">
              You cannot view previous messages or send new ones.
            </p>
          </div>
        ) : encryptionKey ? (
          /* Normal Chat View */
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-500">
                <p>No messages yet. Start the conversation!</p>
              </div>
            ) : (
              messages.map((msg) => {
                const isSender = msg.isSender;
                return (
                  <div
                    key={msg.id}
                    className={`flex ${
                      isSender ? "justify-end" : "justify-start"
                    }`}
                  >
                    <div
                      className={`px-4 py-2 rounded-2xl max-w-xs lg:max-w-lg break-words ${
                        isSender ? "bg-red-700" : "bg-gray-600"
                      }`}
                    >
                      {msg?.image && (
                        <img
                          src={msg.image}
                          alt="sent"
                          className="rounded-lg mb-2 max-w-full cursor-pointer"
                          onClick={() => setFullScreenImage(msg.image)}
                        />
                      )}
                      {msg?.text && <p className="text-white">{msg.text}</p>}
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-gray-400">Loading chat...</p>
          </div>
        )}

        {/* Image Preview */}
        {previewImage && !isPrivateProfile && (
          <div className="p-4 bg-gray-900 border-t border-gray-700 flex items-center gap-4">
            <img
              src={previewImage}
              alt="preview"
              className="w-20 h-20 object-cover rounded-lg"
            />
            <button
              onClick={() => setPreviewImage(null)}
              className="px-4 py-2 bg-gray-700 rounded-lg hover:bg-gray-600"
            >
              Cancel
            </button>
          </div>
        )}

        {/* Input Bar - Hidden if profile is private */}
        {!isPrivateProfile && (
          <div className="p-4 border-t border-gray-700 bg-black flex items-center gap-3">
            <input
              type="file"
              accept="image/*"
              className="hidden"
              id="fileInput"
              onChange={handleImageSelect}
            />
            <label
              htmlFor="fileInput"
              className="cursor-pointer p-3 bg-gray-700 rounded-lg hover:bg-gray-600 transition"
            >
              📷
            </label>

            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) =>
                e.key === "Enter" && !e.shiftKey && handleSend()
              }
              placeholder="Type a message..."
              className="flex-1 px-4 py-3 rounded-lg bg-gray-800 text-white focus:outline-none focus:ring-2 focus:ring-red-600"
            />

            <button
              onClick={handleSend}
              disabled={!newMessage.trim() && !previewImage}
              className="px-5 py-3 bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              Send
            </button>
          </div>
        )}
      </div>

      {/* Fullscreen Image Modal */}
      {fullScreenImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50"
          onClick={() => setFullScreenImage(null)}
        >
          <img
            src={fullScreenImage}
            alt="fullscreen"
            className="max-w-full max-h-full object-contain"
          />
          <button
            onClick={() => setFullScreenImage(null)}
            className="absolute top-4 right-4 bg-gray-800 p-3 rounded-full hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
};

export default ChatScreen;
